import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Reveal } from "./Reveal";
import { Plus } from "lucide-react";

const faqs = [
  {
    q: "What markets and sectors do you serve as fractional CFO?",
    a: [
      "We primarily work with early-stage companies across India and the UAE, while also supporting companies globally where there is the right fit. Most engagements are remote-first, with in-person support available for founder sessions, board meetings, investor preparation, or finance process setup.",
      "We are sector-agnostic because the core finance needs — cash planning, budgeting, reporting, controls, investor readiness, and strategic decision-making — are relevant across companies, irrespective of sector.",
    ],
  },
  {
    q: "When should a founder consider fractional CFO support?",
    a: [
      "Founders should consider fractional CFO support once financial decisions start becoming strategic — usually after the first institutional round of capital or in preparation for one.",
      "This includes runway planning, hiring, pricing, investor reporting, fundraising preparation, market expansion, and setting up finance processes beyond basic accounting.",
    ],
  },
  {
    q: "How hands-on is the engagement, and what does a typical engagement look like?",
    a: [
      "We operate as an embedded extension of the team, working directly within the company's existing workflows, tools, and workspaces.",
      "A typical engagement starts with fixing or setting up finance operations SOPs, improving historical accounting and MIS accuracy, and building a reliable financial operating rhythm. Recurring work usually includes financial modelling, budgeting, cash flow management, monthly MIS, and investor reporting.",
      "These numbers then support strategic decisions around fundraising, hiring, pricing, expansion, and capital allocation.",
    ],
  },
  {
    q: "What kind of founders is venture incubation designed for?",
    a: [
      "Venture incubation is designed for deeptech and technical founders in India working on technical breakthroughs who need long-term partners to help turn them into commercially viable, investor-backable companies.",
      "The ideal stage is pre-incorporation or before institutional capital has been raised, where there is still room to shape the company thesis, business model, operating roadmap, and fundraising narrative.",
    ],
  },
  {
    q: "What companies do you invest in?",
    a: [
      "Angel capital is selective and separate from advisory work. We write early cheques, typically between INR 5L–25L, where there is strong conviction, clear alignment, and an opportunity to add value beyond money.",
      "Investment decisions are made independently and are usually focused on inception-stage companies where we can be meaningfully helpful across finance, strategy, fundraising, and early operating decisions. Where relevant, we also bring in industry specialists and strategic relationships through the RCS Advisors network.",
    ],
  },
];

const FAQ = () => {
  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative py-24 md:py-32 border-t border-ink/15"
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal direction="up">
              <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink/60">
                FAQs
              </span>
            </Reveal>
            <Reveal direction="up" delay={0.1}>
              <h2 className="mt-4 font-display font-black uppercase text-[clamp(2rem,5vw,4rem)] leading-[0.95] tracking-[-0.02em] text-ink">
                Our <br />
                <span className="italic font-medium normal-case text-ink/60">operating model.</span>
              </h2>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal direction="up" delay={0.15}>
              <Accordion
                type="single"
                collapsible
                className="border-t border-ink/20"
                data-testid="faq-accordion"
              >
                {faqs.map((f, i) => (
                  <AccordionItem
                    key={i}
                    value={`item-${i}`}
                    className="border-b border-ink/20"
                    data-testid={`faq-item-${i}`}
                  >
                    <AccordionTrigger className="group py-6 hover:no-underline">
                      <div className="flex items-start gap-5 text-left">
                        <span className="font-mono text-xs text-ink/45 mt-1.5 shrink-0">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className="font-display font-bold text-xl md:text-2xl tracking-[-0.01em] text-ink leading-snug">
                          {f.q}
                        </span>
                      </div>
                      <Plus className="ml-4 w-5 h-5 shrink-0 text-ink transition-transform duration-300 group-data-[state=open]:rotate-45" />
                    </AccordionTrigger>
                    <AccordionContent className="pb-6 pl-12 pr-2">
                      <div className="space-y-4">
                        {(Array.isArray(f.a) ? f.a : [f.a]).map((para, idx) => (
                          <p
                            key={idx}
                            className="font-sans-pro text-base md:text-lg text-ink/70 leading-relaxed"
                          >
                            {para}
                          </p>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
